"use client";

import { useState } from "react";
import { cn } from "@/lib/utils";
import { Flag } from "@/components/shared/flags";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft } from "lucide-react";
import Link from "next/link";
import type {
  Zapas,
  Krajina,
  Gol,
  Trest,
  ZapasStatistikyTim,
  ZapasHracStatistiky,
  ZapasZostava,
} from "@/types/database";

type Tab = "prehlad" | "statistiky" | "zostavy" | "hraci";

interface ZapasDetailKlientProps {
  zapas: Zapas & { domaciKrajina: Krajina; hostiKrajina: Krajina };
  goly: Gol[];
  tresty: Trest[];
  domaciStat: ZapasStatistikyTim | null;
  hostiStat: ZapasStatistikyTim | null;
  domaciHraci: ZapasHracStatistiky[];
  hostiHraci: ZapasHracStatistiky[];
  domaciZostava: ZapasZostava[];
  hostiZostava: ZapasZostava[];
  maData: boolean;
}

const TABS: { key: Tab; label: string }[] = [
  { key: "prehlad", label: "Prehlad" },
  { key: "statistiky", label: "Statistiky" },
  { key: "zostavy", label: "Zostavy" },
  { key: "hraci", label: "Hraci" },
];

function formatCasNaLade(val: string | null): string {
  if (!val) return "-";
  const m = val.match(/(\d+):(\d+):(\d+)/);
  if (m) {
    const hours = parseInt(m[1], 10);
    const mins = parseInt(m[2], 10);
    const secs = m[3];
    const totalMins = hours * 60 + mins;
    return `${totalMins}:${secs}`;
  }
  return val;
}

export function ZapasDetailKlient({
  zapas,
  goly,
  tresty,
  domaciStat,
  hostiStat,
  domaciHraci,
  hostiHraci,
  domaciZostava,
  hostiZostava,
  maData,
}: ZapasDetailKlientProps) {
  const [tab, setTab] = useState<Tab>("prehlad");

  const domaci = zapas.domaciKrajina;
  const hostia = zapas.hostiKrajina;
  const jeUkonceny = zapas.stav === "ukonceny";

  if (!maData) return null;

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        href="/zapasy"
        className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Zapasy
      </Link>

      {/* Score header */}
      <div className="rounded-2xl border border-muted/20 bg-background/80 backdrop-blur-sm p-6 md:p-8 shadow-sm">
        <p className="text-xs font-black text-muted-foreground uppercase tracking-[0.2em] text-center mb-6">
          {zapas.faza === "zakladna" ? "Skupinova faza" : zapas.faza} •{" "}
          {new Date(zapas.datum_cas).toLocaleDateString("sk-SK", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
          {zapas.miesto ? ` • ${zapas.miesto}` : ""}
        </p>

        <div className="flex items-center justify-center gap-4 sm:gap-8 md:gap-12">
          <Link
            href={`/krajina/${domaci.kod.toLowerCase()}`}
            className="flex flex-col items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <Flag
              kod={domaci.kod}
              size={48}
              className="shadow-md rounded-lg border border-muted/20"
            />
            <span
              className={cn(
                "text-xl sm:text-2xl font-black uppercase tracking-tighter hover:text-primary transition-colors",
                domaci.kod === "SVK" ? "text-primary" : "text-foreground",
              )}
            >
              {domaci.kod}
            </span>
          </Link>

          <div className="flex flex-col items-center gap-1.5">
            {jeUkonceny && zapas.skore_domaci !== null ? (
              <>
                <div className="text-5xl sm:text-7xl font-black tracking-tighter tabular-nums leading-none">
                  <span
                    className={cn(
                      zapas.skore_domaci > (zapas.skore_hosti ?? 0)
                        ? "text-primary"
                        : "text-muted-foreground/50",
                    )}
                  >
                    {zapas.skore_domaci}
                  </span>
                  <span className="text-muted-foreground/20 mx-2">:</span>
                  <span
                    className={cn(
                      (zapas.skore_hosti ?? 0) > zapas.skore_domaci
                        ? "text-primary"
                        : "text-muted-foreground/50",
                    )}
                  >
                    {zapas.skore_hosti}
                  </span>
                </div>
                <Badge
                  variant="outline"
                  className="rounded-full px-4 py-1 text-[9px] font-black uppercase tracking-widest bg-status-success/10 text-status-success border-status-success/20"
                >
                  Koniec
                </Badge>
              </>
            ) : (
              <div className="text-3xl font-black text-muted-foreground/30">
                VS
              </div>
            )}
          </div>

          <Link
            href={`/krajina/${hostia.kod.toLowerCase()}`}
            className="flex flex-col items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <Flag
              kod={hostia.kod}
              size={48}
              className="shadow-md rounded-lg border border-muted/20"
            />
            <span
              className={cn(
                "text-xl sm:text-2xl font-black uppercase tracking-tighter hover:text-primary transition-colors",
                hostia.kod === "SVK" ? "text-primary" : "text-foreground",
              )}
            >
              {hostia.kod}
            </span>
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted/20 rounded-xl p-1 border border-muted/10">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              "flex-1 py-2.5 px-3 text-xs font-black uppercase tracking-widest rounded-lg transition-all",
              tab === t.key
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "prehlad" && (
        <PrehladTab
          goly={goly}
          tresty={tresty}
          domaci={domaci}
          hostia={hostia}
          domaciId={zapas.domaci_krajina_id}
        />
      )}
      {tab === "statistiky" && domaciStat && hostiStat && (
        <StatistikyTab
          domaciStat={domaciStat}
          hostiStat={hostiStat}
          domaci={domaci}
          hostia={hostia}
        />
      )}
      {tab === "zostavy" && (
        <ZostavyTab
          domaciZostava={domaciZostava}
          hostiZostava={hostiZostava}
          domaci={domaci}
          hostia={hostia}
        />
      )}
      {tab === "hraci" && (
        <HraciTab
          domaciHraci={domaciHraci}
          hostiHraci={hostiHraci}
          domaci={domaci}
          hostia={hostia}
        />
      )}
    </div>
  );
}

/* ============================================ */
/* PREHLAD TAB - Timeline golov a trestov       */
/* ============================================ */

interface UdalostTimeline {
  cas: string | null;
  tretina: number | null;
  typ: "gol" | "trest";
  krajina_id: string;
  hrac_meno: string;
  detail: string;
  golTyp?: string | null;
}

function PrehladTab({
  goly,
  tresty,
  domaci,
  hostia,
  domaciId,
}: {
  goly: Gol[];
  tresty: Trest[];
  domaci: Krajina;
  hostia: Krajina;
  domaciId: string;
}) {
  const udalostiPodTretiny = new Map<number, UdalostTimeline[]>();

  for (const g of goly) {
    const t = g.tretina ?? 0;
    const arr = udalostiPodTretiny.get(t) || [];
    const asistText =
      g.asistenti && g.asistenti.length > 0 ? g.asistenti.join(" + ") : "";
    const typTag = g.typ !== "regulerny" ? g.typ : null;
    arr.push({
      cas: g.cas,
      tretina: g.tretina,
      typ: "gol",
      krajina_id: g.krajina_id,
      hrac_meno: g.hrac_meno,
      detail: asistText,
      golTyp: typTag,
    });
    udalostiPodTretiny.set(t, arr);
  }

  for (const tr of tresty) {
    const t = tr.tretina ?? 0;
    const arr = udalostiPodTretiny.get(t) || [];
    arr.push({
      cas: tr.cas,
      tretina: tr.tretina,
      typ: "trest",
      krajina_id: tr.krajina_id,
      hrac_meno: tr.hrac_meno,
      detail: `${tr.typ_trestu} (${tr.minuty} min)`,
      golTyp: null,
    });
    udalostiPodTretiny.set(t, arr);
  }

  // Sort events within each period by time
  for (const [key, arr] of udalostiPodTretiny) {
    arr.sort((a, b) => (a.cas || "").localeCompare(b.cas || ""));
    udalostiPodTretiny.set(key, arr);
  }

  const tretiny = [1, 2, 3].filter((t) => udalostiPodTretiny.has(t));

  const skorePoTretine = new Map<number, string>();
  for (const t of [1, 2, 3]) {
    const golyTretina = goly.filter((g) => g.tretina === t);
    const dGoly = golyTretina.filter((g) => g.krajina_id === domaciId).length;
    const hGoly = golyTretina.filter((g) => g.krajina_id !== domaciId).length;
    skorePoTretine.set(t, `${dGoly} - ${hGoly}`);
  }

  return (
    <div className="space-y-6">
      {tretiny.map((tretina) => {
        const udalosti = udalostiPodTretiny.get(tretina) || [];
        return (
          <div key={tretina} className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-black uppercase tracking-[0.15em] text-muted-foreground">
                {tretina}. tretina
              </h3>
              <span className="text-xs font-black text-muted-foreground/60 tabular-nums">
                {skorePoTretine.get(tretina)}
              </span>
            </div>
            <div className="space-y-2">
              {udalosti.map((u, i) => {
                const jeDomaci = u.krajina_id === domaciId;
                const kraj = jeDomaci ? domaci : hostia;
                return (
                  <div
                    key={`${tretina}-${i}`}
                    className={cn(
                      "flex items-start gap-3 p-3 rounded-xl border",
                      u.typ === "gol"
                        ? "bg-status-success/5 border-status-success/10"
                        : "bg-red-500/5 border-red-500/10",
                    )}
                  >
                    <span className="text-xs font-black tabular-nums text-muted-foreground w-10 shrink-0 pt-0.5">
                      {u.cas || "-"}
                    </span>
                    <div
                      className={cn(
                        "w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-[9px] font-black",
                        u.typ === "gol"
                          ? "bg-status-success/20 text-status-success"
                          : "bg-red-500/20 text-red-500",
                      )}
                    >
                      {u.typ === "gol" ? "G" : "P"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Flag kod={kraj.kod} size={14} className="rounded" />
                        <span className="font-black text-sm text-foreground">
                          {u.hrac_meno}
                        </span>
                        {u.golTyp && (
                          <span
                            className={cn(
                              "text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full",
                              u.golTyp === "presilova_hra"
                                ? "bg-amber-500/15 text-amber-600 border border-amber-500/20"
                                : u.golTyp === "prazdna_branka"
                                  ? "bg-blue-500/15 text-blue-600 border border-blue-500/20"
                                  : u.golTyp === "oslabenie"
                                    ? "bg-purple-500/15 text-purple-600 border border-purple-500/20"
                                    : "bg-muted/30 text-muted-foreground border border-muted/20",
                            )}
                          >
                            {formatTypGolu(u.golTyp)}
                          </span>
                        )}
                      </div>
                      {u.detail && (
                        <p className="text-[11px] text-muted-foreground font-medium mt-0.5">
                          {u.detail}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {tretiny.length === 0 && (
        <p className="text-center text-muted-foreground text-sm py-8">
          Ziadne udalosti
        </p>
      )}
    </div>
  );
}

function formatTypGolu(typ: string): string {
  const map: Record<string, string> = {
    presilova_hra: "Presilova hra",
    oslabenie: "Oslabenie",
    prazdna_branka: "Prazdna branka",
    predlzenie: "Predlzenie",
    najazdy: "Najazdy",
  };
  return map[typ] || typ;
}

/* ============================================ */
/* STATISTIKY TAB - Timove porovnanie           */
/* ============================================ */

function StatistikyTab({
  domaciStat,
  hostiStat,
  domaci,
  hostia,
}: {
  domaciStat: ZapasStatistikyTim;
  hostiStat: ZapasStatistikyTim;
  domaci: Krajina;
  hostia: Krajina;
}) {
  const dSNB = domaciStat.strely_na_branku;
  const hSNB = hostiStat.strely_na_branku;
  const dZakroky = domaciStat.zakroky_brankara;
  const hZakroky = hostiStat.zakroky_brankara;
  const dGolyDane = hSNB > 0 ? hSNB - hZakroky : 0;
  const hGolyDane = dSNB > 0 ? dSNB - dZakroky : 0;

  const pctFrac = (a: number, b: number) =>
    b > 0 ? `${((a / b) * 100).toFixed(1)}% (${a}/${b})` : "-";

  const dPP = domaciStat.goly_v_presilovke;
  const hPP = hostiStat.goly_v_presilovke;
  const dPPchances = hostiStat.vylucenia;
  const hPPchances = domaciStat.vylucenia;
  const dPKchances = domaciStat.vylucenia;
  const hPKchances = hostiStat.vylucenia;
  const dPKgolyPustene = hPP;
  const hPKgolyPustene = dPP;

  // Helper funkcie pre výpočet percent
  const parsePercento = (text: string): number => {
    const match = text.match(/(\d+(?:\.\d+)?)\s*%/);
    return match ? Math.min(100, Math.max(0, parseFloat(match[1]))) : 0;
  };

  const clamp = (value: number, min: number, max: number): number =>
    Math.min(max, Math.max(min, value));

  // Typ pre riadok štatistiky
  type StatistikyRow = {
    label: string;
    domaciRaw: string | number;
    hostiaRaw: string | number;
    domaciPct: number;
    hostiaPct: number;
    dominant: "domaci" | "hostia" | "rovnako";
    typ: "pocet" | "percento" | "textPercento" | "nezname";
  };

  const rows: StatistikyRow[] = [
    {
      label: "Strely na branku",
      domaciRaw: dSNB,
      hostiaRaw: hSNB,
      domaciPct:
        dSNB === 0 && hSNB === 0 ? 0 : (dSNB / Math.max(dSNB, hSNB)) * 100,
      hostiaPct:
        dSNB === 0 && hSNB === 0 ? 0 : (hSNB / Math.max(dSNB, hSNB)) * 100,
      dominant: dSNB > hSNB ? "domaci" : dSNB < hSNB ? "hostia" : "rovnako",
      typ: "pocet",
    },
    {
      label: "Strely mimo branu",
      domaciRaw: domaciStat.strely_mimo,
      hostiaRaw: hostiStat.strely_mimo,
      domaciPct:
        domaciStat.strely_mimo === 0 && hostiStat.strely_mimo === 0
          ? 0
          : (domaciStat.strely_mimo /
              Math.max(domaciStat.strely_mimo, hostiStat.strely_mimo)) *
            100,
      hostiaPct:
        domaciStat.strely_mimo === 0 && hostiStat.strely_mimo === 0
          ? 0
          : (hostiStat.strely_mimo /
              Math.max(domaciStat.strely_mimo, hostiStat.strely_mimo)) *
            100,
      dominant:
        domaciStat.strely_mimo > hostiStat.strely_mimo
          ? "domaci"
          : domaciStat.strely_mimo < hostiStat.strely_mimo
            ? "hostia"
            : "rovnako",
      typ: "pocet",
    },
    {
      label: "Uspesnost strelby",
      domaciRaw: pctFrac(dGolyDane > 0 ? dGolyDane : 0, dSNB),
      hostiaRaw: pctFrac(hGolyDane > 0 ? hGolyDane : 0, hSNB),
      domaciPct: parsePercento(pctFrac(dGolyDane > 0 ? dGolyDane : 0, dSNB)),
      hostiaPct: parsePercento(pctFrac(hGolyDane > 0 ? hGolyDane : 0, hSNB)),
      dominant:
        parsePercento(pctFrac(dGolyDane > 0 ? dGolyDane : 0, dSNB)) >
        parsePercento(pctFrac(hGolyDane > 0 ? hGolyDane : 0, hSNB))
          ? "domaci"
          : parsePercento(pctFrac(dGolyDane > 0 ? dGolyDane : 0, dSNB)) <
              parsePercento(pctFrac(hGolyDane > 0 ? hGolyDane : 0, hSNB))
            ? "hostia"
            : "rovnako",
      typ: "textPercento",
    },
    {
      label: "Zablokovane strely",
      domaciRaw: domaciStat.zablokovane_strely,
      hostiaRaw: hostiStat.zablokovane_strely,
      domaciPct:
        domaciStat.zablokovane_strely === 0 &&
        hostiStat.zablokovane_strely === 0
          ? 0
          : (domaciStat.zablokovane_strely /
              Math.max(
                domaciStat.zablokovane_strely,
                hostiStat.zablokovane_strely,
              )) *
            100,
      hostiaPct:
        domaciStat.zablokovane_strely === 0 &&
        hostiStat.zablokovane_strely === 0
          ? 0
          : (hostiStat.zablokovane_strely /
              Math.max(
                domaciStat.zablokovane_strely,
                hostiStat.zablokovane_strely,
              )) *
            100,
      dominant:
        domaciStat.zablokovane_strely > hostiStat.zablokovane_strely
          ? "domaci"
          : domaciStat.zablokovane_strely < hostiStat.zablokovane_strely
            ? "hostia"
            : "rovnako",
      typ: "pocet",
    },
    {
      label: "Zakroky brankara",
      domaciRaw: dZakroky,
      hostiaRaw: hZakroky,
      domaciPct:
        dZakroky === 0 && hZakroky === 0
          ? 0
          : (dZakroky / Math.max(dZakroky, hZakroky)) * 100,
      hostiaPct:
        dZakroky === 0 && hZakroky === 0
          ? 0
          : (hZakroky / Math.max(dZakroky, hZakroky)) * 100,
      dominant:
        dZakroky > hZakroky
          ? "domaci"
          : dZakroky < hZakroky
            ? "hostia"
            : "rovnako",
      typ: "pocet",
    },
    {
      label: "Uspesnost zakrokov",
      domaciRaw: pctFrac(dZakroky, hSNB),
      hostiaRaw: pctFrac(hZakroky, dSNB),
      domaciPct: parsePercento(pctFrac(dZakroky, hSNB)),
      hostiaPct: parsePercento(pctFrac(hZakroky, dSNB)),
      dominant:
        parsePercento(pctFrac(dZakroky, hSNB)) >
        parsePercento(pctFrac(hZakroky, dSNB))
          ? "domaci"
          : parsePercento(pctFrac(dZakroky, hSNB)) <
              parsePercento(pctFrac(hZakroky, dSNB))
            ? "hostia"
            : "rovnako",
      typ: "textPercento",
    },
    {
      label: "Vylucenia",
      domaciRaw: domaciStat.vylucenia,
      hostiaRaw: hostiStat.vylucenia,
      domaciPct:
        domaciStat.vylucenia === 0 && hostiStat.vylucenia === 0
          ? 0
          : (domaciStat.vylucenia /
              Math.max(domaciStat.vylucenia, hostiStat.vylucenia)) *
            100,
      hostiaPct:
        domaciStat.vylucenia === 0 && hostiStat.vylucenia === 0
          ? 0
          : (hostiStat.vylucenia /
              Math.max(domaciStat.vylucenia, hostiStat.vylucenia)) *
            100,
      dominant:
        domaciStat.vylucenia > hostiStat.vylucenia
          ? "domaci"
          : domaciStat.vylucenia < hostiStat.vylucenia
            ? "hostia"
            : "rovnako",
      typ: "pocet",
    },
    {
      label: "Vylucenia (min.)",
      domaciRaw: domaciStat.vylucenia_minuty,
      hostiaRaw: hostiStat.vylucenia_minuty,
      domaciPct:
        domaciStat.vylucenia_minuty === 0 && hostiStat.vylucenia_minuty === 0
          ? 0
          : (domaciStat.vylucenia_minuty /
              Math.max(
                domaciStat.vylucenia_minuty,
                hostiStat.vylucenia_minuty,
              )) *
            100,
      hostiaPct:
        domaciStat.vylucenia_minuty === 0 && hostiStat.vylucenia_minuty === 0
          ? 0
          : (hostiStat.vylucenia_minuty /
              Math.max(
                domaciStat.vylucenia_minuty,
                hostiStat.vylucenia_minuty,
              )) *
            100,
      dominant:
        domaciStat.vylucenia_minuty > hostiStat.vylucenia_minuty
          ? "domaci"
          : domaciStat.vylucenia_minuty < hostiStat.vylucenia_minuty
            ? "hostia"
            : "rovnako",
      typ: "pocet",
    },
    {
      label: "Goly v presilovkach",
      domaciRaw: dPP,
      hostiaRaw: hPP,
      domaciPct: dPP === 0 && hPP === 0 ? 0 : (dPP / Math.max(dPP, hPP)) * 100,
      hostiaPct: dPP === 0 && hPP === 0 ? 0 : (hPP / Math.max(dPP, hPP)) * 100,
      dominant: dPP > hPP ? "domaci" : dPP < hPP ? "hostia" : "rovnako",
      typ: "pocet",
    },
    {
      label: "Goly v oslabeniach",
      domaciRaw: domaciStat.goly_v_oslabeni,
      hostiaRaw: hostiStat.goly_v_oslabeni,
      domaciPct:
        domaciStat.goly_v_oslabeni === 0 && hostiStat.goly_v_oslabeni === 0
          ? 0
          : (domaciStat.goly_v_oslabeni /
              Math.max(domaciStat.goly_v_oslabeni, hostiStat.goly_v_oslabeni)) *
            100,
      hostiaPct:
        domaciStat.goly_v_oslabeni === 0 && hostiStat.goly_v_oslabeni === 0
          ? 0
          : (hostiStat.goly_v_oslabeni /
              Math.max(domaciStat.goly_v_oslabeni, hostiStat.goly_v_oslabeni)) *
            100,
      dominant:
        domaciStat.goly_v_oslabeni > hostiStat.goly_v_oslabeni
          ? "domaci"
          : domaciStat.goly_v_oslabeni < hostiStat.goly_v_oslabeni
            ? "hostia"
            : "rovnako",
      typ: "pocet",
    },
    {
      label: "Uspesnost presiloviek",
      domaciRaw: pctFrac(dPP, dPPchances),
      hostiaRaw: pctFrac(hPP, hPPchances),
      domaciPct: parsePercento(pctFrac(dPP, dPPchances)),
      hostiaPct: parsePercento(pctFrac(hPP, hPPchances)),
      dominant:
        parsePercento(pctFrac(dPP, dPPchances)) >
        parsePercento(pctFrac(hPP, hPPchances))
          ? "domaci"
          : parsePercento(pctFrac(dPP, dPPchances)) <
              parsePercento(pctFrac(hPP, hPPchances))
            ? "hostia"
            : "rovnako",
      typ: "textPercento",
    },
    {
      label: "Uspesnost oslaboviek",
      domaciRaw:
        dPKchances > 0 ? pctFrac(dPKchances - dPKgolyPustene, dPKchances) : "-",
      hostiaRaw:
        hPKchances > 0 ? pctFrac(hPKchances - hPKgolyPustene, hPKchances) : "-",
      domaciPct: parsePercento(
        dPKchances > 0 ? pctFrac(dPKchances - dPKgolyPustene, dPKchances) : "-",
      ),
      hostiaPct: parsePercento(
        hPKchances > 0 ? pctFrac(hPKchances - hPKgolyPustene, hPKchances) : "-",
      ),
      dominant:
        parsePercento(
          dPKchances > 0
            ? pctFrac(dPKchances - dPKgolyPustene, dPKchances)
            : "-",
        ) >
        parsePercento(
          hPKchances > 0
            ? pctFrac(hPKchances - hPKgolyPustene, hPKchances)
            : "-",
        )
          ? "domaci"
          : parsePercento(
                dPKchances > 0
                  ? pctFrac(dPKchances - dPKgolyPustene, dPKchances)
                  : "-",
              ) <
              parsePercento(
                hPKchances > 0
                  ? pctFrac(hPKchances - hPKgolyPustene, hPKchances)
                  : "-",
              )
            ? "hostia"
            : "rovnako",
      typ: "textPercento",
    },
    {
      label: "Vyhrate vhadzovania",
      domaciRaw: domaciStat.vyhrate_vhadzovania,
      hostiaRaw: hostiStat.vyhrate_vhadzovania,
      domaciPct:
        domaciStat.vyhrate_vhadzovania === 0 &&
        hostiStat.vyhrate_vhadzovania === 0
          ? 0
          : (domaciStat.vyhrate_vhadzovania /
              Math.max(
                domaciStat.vyhrate_vhadzovania,
                hostiStat.vyhrate_vhadzovania,
              )) *
            100,
      hostiaPct:
        domaciStat.vyhrate_vhadzovania === 0 &&
        hostiStat.vyhrate_vhadzovania === 0
          ? 0
          : (hostiStat.vyhrate_vhadzovania /
              Math.max(
                domaciStat.vyhrate_vhadzovania,
                hostiStat.vyhrate_vhadzovania,
              )) *
            100,
      dominant:
        domaciStat.vyhrate_vhadzovania > hostiStat.vyhrate_vhadzovania
          ? "domaci"
          : domaciStat.vyhrate_vhadzovania < hostiStat.vyhrate_vhadzovania
            ? "hostia"
            : "rovnako",
      typ: "pocet",
    },
    {
      label: "Uspesnost na vhadzovaniach (%)",
      domaciRaw: `${domaciStat.vhadzovania_percento}`,
      hostiaRaw: `${hostiStat.vhadzovania_percento}`,
      domaciPct: clamp(domaciStat.vhadzovania_percento, 0, 100),
      hostiaPct: clamp(hostiStat.vhadzovania_percento, 0, 100),
      dominant:
        domaciStat.vhadzovania_percento > hostiStat.vhadzovania_percento
          ? "domaci"
          : domaciStat.vhadzovania_percento < hostiStat.vhadzovania_percento
            ? "hostia"
            : "rovnako",
      typ: "percento",
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between px-1 mb-3">
        <div className="flex items-center gap-2">
          <Flag kod={domaci.kod} size={16} className="rounded" />
          <span className="text-xs font-black uppercase">{domaci.kod}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-black uppercase">{hostia.kod}</span>
          <Flag kod={hostia.kod} size={16} className="rounded" />
        </div>
      </div>

      <div className="space-y-1">
        {rows.map((row) => {
          // Skryť riadok "Goly v presilovkách" ak obe hodnoty sú 0
          if (
            row.label === "Goly v presilovkach" &&
            row.domaciRaw === 0 &&
            row.hostiaRaw === 0
          ) {
            return null;
          }

          // Pre percentuálne hodnoty: priama percentuálna hodnota
          // Pre počtové hodnoty: max = 100%, menšie = percentuálny podiel
          const isPercent =
            row.typ === "percento" || row.typ === "textPercento";
          let domaciBarPct: number;
          let hostiaBarPct: number;

          if (isPercent) {
            // Percentuálne hodnoty: priama hodnota (20% = 20% šírky)
            domaciBarPct = row.domaciPct;
            hostiaBarPct = row.hostiaPct;
          } else {
            // Počtové hodnoty: max = 100%, menšie = percentuálny podiel
            const max = Math.max(row.domaciPct, row.hostiaPct);
            domaciBarPct = max > 0 ? (row.domaciPct / max) * 100 : 0;
            hostiaBarPct = max > 0 ? (row.hostiaPct / max) * 100 : 0;
          }

          // Šírka pruhu = polovica tracku × (pct / 100)
          const domaciW = domaciBarPct / 2;
          const hostiaW = hostiaBarPct / 2;

          // Farby: víťaz = výrazná červená, prehralo = slabá červená, rovnako = stredná
          const domaciColor =
            row.dominant === "domaci"
              ? "bg-red-500"
              : row.dominant === "rovnako"
                ? "bg-red-500/50"
                : "bg-red-500/20";

          const hostiaColor =
            row.dominant === "hostia"
              ? "bg-red-500"
              : row.dominant === "rovnako"
                ? "bg-red-500/50"
                : "bg-red-500/20";

          // Čísla vždy čierne, bez zvýraznenia
          const textColor = "text-foreground";

          return (
            <div key={row.label}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className={cn("font-black tabular-nums", textColor)}>
                  {row.domaciRaw}
                </span>
                <span className="font-medium text-muted-foreground">
                  {row.label}
                </span>
                <span className={cn("font-black tabular-nums", textColor)}>
                  {row.hostiaRaw}
                </span>
              </div>

              {/* Vždy zobraziť graf */}
              <div className="relative h-2 bg-muted/20 rounded-full overflow-hidden">
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background/80 z-10" />

                <div
                  className={cn(
                    "absolute left-0 top-0 bottom-0 transition-all duration-500 ease-out rounded-l-full",
                    domaciColor,
                  )}
                  style={{ width: `${domaciW}%`, maxWidth: "50%" }}
                />

                <div
                  className={cn(
                    "absolute right-0 top-0 bottom-0 transition-all duration-500 ease-out rounded-r-full",
                    hostiaColor,
                  )}
                  style={{ width: `${hostiaW}%`, maxWidth: "50%" }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================ */
/* ZOSTAVY TAB - Formacie oboch timov           */
/* ============================================ */

function ZostavyTab({
  domaciZostava,
  hostiZostava,
  domaci,
  hostia,
}: {
  domaciZostava: ZapasZostava[];
  hostiZostava: ZapasZostava[];
  domaci: Krajina;
  hostia: Krajina;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <ZostavaTimu zostava={domaciZostava} krajina={domaci} />
      <ZostavaTimu zostava={hostiZostava} krajina={hostia} />
    </div>
  );
}

function ZostavaTimu({
  zostava,
  krajina,
}: {
  zostava: ZapasZostava[];
  krajina: Krajina;
}) {
  // Oddelenie brankárov - iba hlavný brankár (formácia 1) v sekcii Brankari
  const hlavnyBrankar = zostava.find(
    (z) => z.pozicia_v_zostave === "B" && z.formacia === 1,
  );
  const hraci = zostava.filter(
    (z) => !(z.pozicia_v_zostave === "B" && z.formacia === 1),
  );

  const formacie = new Map<number, ZapasZostava[]>();
  for (const z of hraci) {
    const f = z.formacia ?? 0;
    const arr = formacie.get(f) || [];
    arr.push(z);
    formacie.set(f, arr);
  }

  const sortedKeys = [...formacie.keys()].sort((a, b) => {
    if (a === 0) return 1;
    if (b === 0) return -1;
    return a - b;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Flag kod={krajina.kod} size={20} className="rounded" />
        <h3 className="text-sm font-black uppercase tracking-wider">
          {krajina.nazov}
        </h3>
      </div>

      {/* Hlavný brankár - samostatná sekcia */}
      {hlavnyBrankar && (
        <div className="space-y-1.5">
          <h4 className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
            Brankari
          </h4>
          <div className="space-y-1">
            <div
              key={hlavnyBrankar.id}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/10 border border-muted/10"
            >
              <span className="text-[9px] font-black uppercase w-4 text-amber-500">
                B
              </span>
              <span className="text-sm font-bold text-foreground flex-1">
                {hlavnyBrankar.hrac_meno}
              </span>
              {hlavnyBrankar.je_kapitan && (
                <Badge
                  variant="outline"
                  className="text-[8px] font-black px-1.5 py-0 rounded-full border-primary/30 text-primary"
                >
                  C
                </Badge>
              )}
              {hlavnyBrankar.je_asistent && (
                <Badge
                  variant="outline"
                  className="text-[8px] font-black px-1.5 py-0 rounded-full border-muted-foreground/30"
                >
                  A
                </Badge>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Formácie hráčov */}
      {sortedKeys.map((f) => {
        const hraciVFormacii = formacie.get(f) || [];

        // Zoradenie: v náhradníkoch brankár prvý, potom obrancovia, potom útočníci
        // Vo formáciách obrancovia prví, potom útočníci
        hraciVFormacii.sort((a, b) => {
          const poziciaOrder = f === 0 ? { B: 0, O: 1, U: 2 } : { O: 0, U: 1 };
          const aOrder =
            poziciaOrder[a.pozicia_v_zostave as keyof typeof poziciaOrder] ??
            (f === 0 ? 3 : 2);
          const bOrder =
            poziciaOrder[b.pozicia_v_zostave as keyof typeof poziciaOrder] ??
            (f === 0 ? 3 : 2);
          return aOrder - bOrder;
        });

        const nazovFormacie = f === 0 ? "Nahradnici" : `Formacia ${f}`;

        return (
          <div key={f} className="space-y-1.5">
            <h4 className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
              {nazovFormacie}
            </h4>
            <div className="space-y-1">
              {hraciVFormacii.map((h) => (
                <div
                  key={h.id}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/10 border border-muted/10"
                >
                  <span
                    className={cn(
                      "text-[9px] font-black uppercase w-4",
                      h.pozicia_v_zostave === "O"
                        ? "text-blue-500"
                        : h.pozicia_v_zostave === "B"
                          ? "text-amber-500"
                          : "text-status-success",
                    )}
                  >
                    {h.pozicia_v_zostave || "-"}
                  </span>
                  <span className="text-sm font-bold text-foreground flex-1">
                    {h.hrac_meno}
                  </span>
                  {h.je_kapitan && (
                    <Badge
                      variant="outline"
                      className="text-[8px] font-black px-1.5 py-0 rounded-full border-primary/30 text-primary"
                    >
                      C
                    </Badge>
                  )}
                  {h.je_asistent && (
                    <Badge
                      variant="outline"
                      className="text-[8px] font-black px-1.5 py-0 rounded-full border-muted-foreground/30"
                    >
                      A
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ============================================ */
/* HRACI TAB - Individualne statistiky          */
/* ============================================ */

type SortKey =
  | "body"
  | "goly"
  | "asistencie"
  | "plus_minus"
  | "trestne_minuty"
  | "strely"
  | "fow"
  | "toi";

function parseTOItoSeconds(val: string | null): number {
  if (!val) return 0;
  const m = val.match(/(\d+):(\d+):(\d+)/);
  if (m)
    return (
      parseInt(m[1], 10) * 3600 + parseInt(m[2], 10) * 60 + parseInt(m[3], 10)
    );
  return 0;
}

function HraciTab({
  domaciHraci,
  hostiHraci,
  domaci,
  hostia,
}: {
  domaciHraci: ZapasHracStatistiky[];
  hostiHraci: ZapasHracStatistiky[];
  domaci: Krajina;
  hostia: Krajina;
}) {
  const [timFilter, setTimFilter] = useState<"vsetci" | "domaci" | "hostia">(
    "vsetci",
  );
  const [sortKey, setSortKey] = useState<SortKey>("body");
  const [sortAsc, setSortAsc] = useState(false);

  const domaciId = domaciHraci[0]?.krajina_id;

  const baseHraci =
    timFilter === "domaci"
      ? domaciHraci
      : timFilter === "hostia"
        ? hostiHraci
        : [...domaciHraci, ...hostiHraci];

  const getSortVal = (h: ZapasHracStatistiky): number => {
    switch (sortKey) {
      case "goly":
        return h.goly;
      case "asistencie":
        return h.asistencie;
      case "body":
        return h.body;
      case "plus_minus":
        return h.plus_minus;
      case "trestne_minuty":
        return h.trestne_minuty;
      case "strely":
        return h.strely;
      case "fow":
        return h.vhadzovania_vyhrate + h.vhadzovania_prehrate;
      case "toi":
        return parseTOItoSeconds(h.cas_na_lade);
      default:
        return h.body;
    }
  };

  const hraci = [...baseHraci].sort((a, b) => {
    const aVal = getSortVal(a);
    const bVal = getSortVal(b);
    const diff = sortAsc ? aVal - bVal : bVal - aVal;
    if (diff !== 0) return diff;
    return b.body - a.body || b.goly - a.goly;
  });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const sortIndicator = (key: SortKey) =>
    sortKey === key ? (sortAsc ? " \u25B2" : " \u25BC") : "";

  const thClass = (key: SortKey) =>
    cn(
      "text-center py-2 px-1 font-black text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors whitespace-nowrap",
      sortKey === key ? "text-foreground" : "",
    );

  return (
    <div className="space-y-4">
      <div className="flex gap-1 bg-muted/10 rounded-lg p-0.5 border border-muted/10">
        {[
          { key: "vsetci" as const, label: "Vsetci" },
          { key: "domaci" as const, label: domaci.kod },
          { key: "hostia" as const, label: hostia.kod },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setTimFilter(f.key)}
            className={cn(
              "flex-1 py-1.5 px-2 text-[10px] font-black uppercase tracking-widest rounded-md transition-all",
              timFilter === f.key
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto -mx-4 px-4">
        <table className="w-full text-xs min-w-[600px]">
          <thead>
            <tr className="border-b border-muted/20">
              <th className="text-left py-2 px-2 font-black text-muted-foreground uppercase tracking-wider">
                Hrac
              </th>
              <th
                className={thClass("goly")}
                onClick={() => handleSort("goly")}
              >
                G{sortIndicator("goly")}
              </th>
              <th
                className={thClass("asistencie")}
                onClick={() => handleSort("asistencie")}
              >
                A{sortIndicator("asistencie")}
              </th>
              <th
                className={thClass("body")}
                onClick={() => handleSort("body")}
              >
                B{sortIndicator("body")}
              </th>
              <th
                className={thClass("plus_minus")}
                onClick={() => handleSort("plus_minus")}
              >
                +/-{sortIndicator("plus_minus")}
              </th>
              <th
                className={thClass("trestne_minuty")}
                onClick={() => handleSort("trestne_minuty")}
              >
                TM{sortIndicator("trestne_minuty")}
              </th>
              <th
                className={thClass("strely")}
                onClick={() => handleSort("strely")}
              >
                S{sortIndicator("strely")}
              </th>
              <th className={thClass("fow")} onClick={() => handleSort("fow")}>
                VhV{sortIndicator("fow")}
              </th>
              <th className={thClass("toi")} onClick={() => handleSort("toi")}>
                TOI{sortIndicator("toi")}
              </th>
            </tr>
          </thead>
          <tbody>
            {hraci.map((h) => {
              const jeDomaci = h.krajina_id === domaciId;
              const fow = h.vhadzovania_vyhrate + h.vhadzovania_prehrate;
              const foPct =
                fow > 0
                  ? `${((h.vhadzovania_vyhrate / fow) * 100).toFixed(0)}%`
                  : "";

              return (
                <tr
                  key={h.id}
                  className="border-b border-muted/10 hover:bg-muted/5"
                >
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-1.5">
                      <Flag
                        kod={jeDomaci ? domaci.kod : hostia.kod}
                        size={12}
                        className="rounded shrink-0"
                      />
                      <span
                        className={cn(
                          "font-bold truncate max-w-[120px]",
                          h.body > 0
                            ? "text-foreground"
                            : "text-muted-foreground",
                        )}
                      >
                        {h.hrac_meno}
                      </span>
                    </div>
                  </td>
                  <td
                    className={cn(
                      "text-center py-2 px-1 font-black tabular-nums",
                      h.goly > 0
                        ? "text-status-success"
                        : "text-muted-foreground/40",
                    )}
                  >
                    {h.goly || "-"}
                  </td>
                  <td
                    className={cn(
                      "text-center py-2 px-1 font-black tabular-nums",
                      h.asistencie > 0
                        ? "text-blue-500"
                        : "text-muted-foreground/40",
                    )}
                  >
                    {h.asistencie || "-"}
                  </td>
                  <td
                    className={cn(
                      "text-center py-2 px-1 font-black tabular-nums",
                      h.body > 0
                        ? "text-foreground"
                        : "text-muted-foreground/40",
                    )}
                  >
                    {h.body || "-"}
                  </td>
                  <td
                    className={cn(
                      "text-center py-2 px-1 font-bold tabular-nums",
                      h.plus_minus > 0
                        ? "text-status-success"
                        : h.plus_minus < 0
                          ? "text-red-500"
                          : "text-muted-foreground/40",
                    )}
                  >
                    {h.plus_minus > 0
                      ? `+${h.plus_minus}`
                      : h.plus_minus || "-"}
                  </td>
                  <td className="text-center py-2 px-1 font-bold tabular-nums text-muted-foreground/60">
                    {h.trestne_minuty || "-"}
                  </td>
                  <td className="text-center py-2 px-1 font-bold tabular-nums text-muted-foreground/60">
                    {h.strely || "-"}
                  </td>
                  <td className="text-center py-2 px-1 font-bold tabular-nums text-muted-foreground/60 whitespace-nowrap">
                    {fow > 0 ? `${h.vhadzovania_vyhrate}/${fow} ${foPct}` : "-"}
                  </td>
                  <td className="text-center py-2 px-1 font-bold tabular-nums text-muted-foreground/60">
                    {formatCasNaLade(h.cas_na_lade)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
